export type LicensingTier = 'DEMO_TEST' | 'SOLO_PRO' | 'AGENCY_SUITE' | 'SYSTEM_OWNER';

export type ModuleId =
  | 'research-hub'
  | 'content-gen'
  | 'content-library'
  | 'publishing-hub'
  | 'automation'
  | 'analytics'
  | 'email-marketing'
  | 'image-gen'
  | 'rss-creator'
  | 'prompt-creator'
  | 'live-chat'
  | 'affiliate-tracker'
  | 'social-scheduler'
  | 'media-studio'
  | 'lead-capture'
  | 'slideshow-builder'
  | 'cover-gen'
  | 'token-management'
  | 'legal-hub'
  | 'live-support'
  | 'team-access'
  | 'campaigns'
  | 'lead-generation'
  | 'marketing-manager';

export interface BusinessProfile {
  businessName: string;
  tagline: string;
  industry: string;
  websiteUrl: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: string;
  brandColorPrimary: string;
  brandColorSecondary: string;
  logoUrl: string;
  businessDescription: string;
  targetAudience: string;
  uniqueSellingProposition: string;
  socialLinks: {
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  tierKey: string;
  priceMonthly: number;
  priceAnnually: number;
  maxSequences: number;
  maxEmailsPerMonth: number;
  maxImagesPerMonth: number;
  maxScheduledPosts: number;
  maxAffiliateLinks: number;
  maxTokensPerMonth: number;
  features: string[];
  isActive: boolean;
  sortOrder: number;
}

export interface WhiteLabelConfig {
  brandName: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  customDomain: string;
  customEmailFromName: string;
  hidePoweredBy: boolean;
}

export interface TenantVisualProfile {
  tenantId: string;
  brandName: string;
  primaryColorHex: string;
  logoAssetPath: string;
  customDomain: string;
  licensingTier: LicensingTier;
  allowedModules: ModuleId[];
  businessProfile?: BusinessProfile;
  whiteLabelConfig?: WhiteLabelConfig;
  subscriptionPlan?: SubscriptionPlan;
}

export interface EncryptedVaultPointers {
  hasOpenAI: boolean;
  hasQuetext: boolean;
  hasNetlify: boolean;
  updatedAt: string | null;
}

export interface TenantBootstrapResponse {
  tenantId: string;
  brandName: string;
  primaryColorHex: string;
  logoAssetPath: string;
  customDomain: string;
  licenseTier: LicensingTier;
  allowedModules: ModuleId[];
}

export const SANDBOX_PROFILE: TenantVisualProfile = {
  tenantId: 'local_sandbox',
  brandName: 'Aurelian Marketing Engine',
  primaryColorHex: '#f59e0b',
  logoAssetPath: '/logo.svg',
  customDomain: 'localhost',
  licensingTier: 'SOLO_PRO',
  allowedModules: ['research-hub', 'content-gen', 'content-library', 'publishing-hub'],
};

export const MODULE_TIER_MAP: Record<LicensingTier, ModuleId[]> = {
  DEMO_TEST: ['research-hub', 'content-gen', 'image-gen', 'lead-capture', 'slideshow-builder', 'live-chat', 'live-support'],
  SOLO_PRO: [
    'research-hub', 'content-gen', 'content-library', 'publishing-hub',
    'image-gen', 'affiliate-tracker', 'social-scheduler', 'lead-capture',
    'slideshow-builder', 'cover-gen', 'live-chat', 'email-marketing',
    'prompt-creator', 'rss-creator', 'token-management',
    'live-support', 'campaigns', 'lead-generation', 'marketing-manager',
  ],
  AGENCY_SUITE: [
    'research-hub', 'content-gen', 'content-library', 'publishing-hub',
    'automation', 'analytics', 'email-marketing', 'affiliate-tracker', 'social-scheduler',
    'image-gen', 'media-studio', 'lead-capture', 'slideshow-builder',
    'cover-gen', 'prompt-creator', 'rss-creator', 'live-chat',
    'token-management', 'legal-hub', 'live-support', 'team-access', 'campaigns', 'lead-generation', 'marketing-manager',
  ],
  SYSTEM_OWNER: [
    'research-hub', 'content-gen', 'content-library', 'publishing-hub',
    'automation', 'analytics', 'email-marketing', 'image-gen',
    'rss-creator', 'prompt-creator', 'live-chat', 'affiliate-tracker', 'social-scheduler',
    'media-studio', 'lead-capture', 'slideshow-builder', 'cover-gen',
    'token-management', 'legal-hub', 'live-support', 'team-access', 'campaigns', 'lead-generation', 'marketing-manager',
  ],
};

export const DEFAULT_BUSINESS_PROFILE: BusinessProfile = {
  businessName: '',
  tagline: '',
  industry: '',
  websiteUrl: '',
  contactEmail: '',
  contactPhone: '',
  businessAddress: '',
  brandColorPrimary: '#f59e0b',
  brandColorSecondary: '#3b82f6',
  logoUrl: '',
  businessDescription: '',
  targetAudience: '',
  uniqueSellingProposition: '',
  socialLinks: {},
};

export const DEFAULT_WHITE_LABEL: WhiteLabelConfig = {
  brandName: 'Aurelian Marketing Engine',
  logoUrl: '',
  primaryColor: '#f59e0b',
  secondaryColor: '#3b82f6',
  customDomain: '',
  customEmailFromName: '',
  hidePoweredBy: false,
};

export const SUBSCRIPTION_TIERS = [
  {
    key: 'FREE',
    name: 'Free',
    priceMonthly: 0,
    priceAnnually: 0,
    color: 'from-slate-500 to-slate-600',
    features: ['5 AI images/month', '5 scheduled posts', '3 affiliate links', 'Solo workspace', 'Content generation', 'Research hub', 'Basic analytics', 'Lead capture forms', 'Live chat widget', 'Shared slideshows', 'Setup wizard', 'System tour', 'No API keys required'],
    limits: { sequences: 1, emails: 0, images: 5, posts: 5, affiliates: 3, tokens: 10000 },
  },
  {
    key: 'STARTER',
    name: 'Starter',
    priceMonthly: 49,
    priceAnnually: 490,
    color: 'from-blue-500 to-cyan-600',
    features: ['30 AI images/month', '30 scheduled posts', '10 affiliate links', '1 team seat', 'Content library', 'Publishing hub', 'Email marketing system', 'Affiliate portal', '20% affiliate commission', 'Podcast generation (5/month)', 'Lead capture forms', 'Live chat widget', 'Shared slideshows', 'Setup wizard', 'System tour', 'Email support', 'No API keys required'],
    limits: { sequences: 5, emails: 0, images: 30, posts: 30, affiliates: 10, tokens: 75000 },
  },
  {
    key: 'PRO',
    name: 'Professional',
    priceMonthly: 149,
    priceAnnually: 1490,
    color: 'from-violet-500 to-purple-600',
    features: ['100 AI images/month', '100 scheduled posts', '50 affiliate links', '5 team seats', 'Advanced analytics', 'Campaigns management', 'AI Agent + Marketing Manager', 'Lead Generation', 'Automation pipeline', 'Email marketing system', 'Affiliate portal', '25% affiliate commission', 'Video generation (uses token budget)', 'Social scheduler & inbox', 'Podcast generation (15/month)', 'Quetext content integrity (plagiarism, AI detection, humanizer)', 'Lead capture forms', 'Live chat widget', 'Shared slideshows', 'Setup wizard', 'System tour', 'Live support', 'No API keys required'],
    limits: { sequences: 20, emails: 0, images: 100, posts: 100, affiliates: 50, tokens: 250000 },
  },
  {
    key: 'AGENCY',
    name: 'Agency',
    priceMonthly: 499,
    priceAnnually: 4990,
    color: 'from-amber-500 to-orange-600',
    features: ['500 AI images/month', '500 scheduled posts', '200 affiliate links', '20 team seats', 'White-label branding', 'AI Agent + Marketing Manager', 'Lead Generation', 'Campaigns management', 'Team access & roles', 'Automation pipeline', 'Email marketing system', 'Affiliate portal', '30% affiliate commission for first 12 months, then 15%', 'Video generation (uses token budget)', 'Social scheduler & inbox', 'Podcast generation (subject to token budget)', 'Quetext content integrity (plagiarism, AI detection, humanizer)', 'Lead capture forms', 'Priority live support', 'Shared slideshows', 'Setup wizard', 'System tour', 'No API keys required'], 
    limits: { sequences: 100, emails: 0, images: 500, posts: 500, affiliates: 200, tokens: 750000 },
  },
  {
    key: 'ENTERPRISE',
    name: 'Enterprise',
    priceMonthly: 999,
    priceAnnually: 9990,
    color: 'from-rose-500 to-pink-600',
    features: ['Up to 9,999 AI images/month', 'Up to 9,999 scheduled posts/month', 'Up to 9,999 affiliate links', '100 team seats', 'White-label branding', 'AI Agent + Marketing Manager', 'Lead Generation', 'Advanced campaigns management', 'Organization-wide team access & roles', 'Enterprise analytics & reporting', 'Automation pipeline', 'Email marketing system', 'Affiliate portal', '30% affiliate commission for first 12 months, then 15%', 'Video generation (uses token budget)', 'Social scheduler & inbox', 'Podcast generation (subject to token budget)', 'Quetext content integrity (plagiarism, AI detection, humanizer)', 'Custom integrations by agreement', 'Lead capture forms', 'Priority live support', 'Shared slideshows', 'Setup wizard', 'No API keys required'], 
    limits: { sequences: 999, emails: 0, images: 9999, posts: 9999, affiliates: 9999, tokens: 2000000 },
  },
] as const;
